/**
 * Servicio de audio para el teléfono SIP
 * Implementa tonos DTMF y tonos de sistema usando Web Audio API
 */
class AudioService {
  constructor() {
    // Verificar si Web Audio API está disponible
    if (window.AudioContext || window.webkitAudioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.isSupported = true;
    } else {
      this.isSupported = false;
      console.warn('Web Audio API no soportada en este navegador');
    }
    
    // Estado de reproducción activa
    this.activeOscillators = [];
    this.activeGainNodes = [];
    
    // Frecuencias DTMF estándar
    this.dtmfFrequencies = {
      '1': [697, 1209],
      '2': [697, 1336],
      '3': [697, 1477],
      '4': [770, 1209],
      '5': [770, 1336],
      '6': [770, 1477],
      '7': [852, 1209],
      '8': [852, 1336],
      '9': [852, 1477],
      '*': [941, 1209],
      '0': [941, 1336],
      '#': [941, 1477]
    };
    
    // Frecuencias para tonos especiales
    this.specialTones = {
      'dial': [350, 440],   // Tono de marcado
      'ring': [440, 480],   // Tono de timbre
      'busy': [480, 620],   // Tono de ocupado
      'end': [400, 400]     // Tono de fin de llamada
    };
    
    // Tonos de sistema activos
    this.dialToneActive = false;
    this.ringToneActive = false;
    this.busyToneActive = false;
    this.dialToneIntervalId = null;
    this.ringToneIntervalId = null;
    this.busyToneIntervalId = null;
  }
  
  /**
   * Genera un tono con las frecuencias dadas
   * @param {Number} freq1 - Primera frecuencia en Hz
   * @param {Number} freq2 - Segunda frecuencia en Hz
   * @param {Number} duration - Duración en segundos (0 para mantenerlo indefinidamente)
   * @param {Number} volume - Volumen (0-1)
   * @returns {Object} Objeto con osciladores y nodos de ganancia
   */
  generateTone(freq1, freq2, duration = 0.1, volume = 0.2) {
    if (!this.isSupported) return null;
    
    try {
      // Crear osciladores
      const osc1 = this.audioContext.createOscillator();
      const osc2 = this.audioContext.createOscillator();
      
      // Configurar frecuencias
      osc1.frequency.value = freq1;
      osc2.frequency.value = freq2;
      osc1.type = 'sine';
      osc2.type = 'sine';
      
      // Crear nodos de ganancia
      const gainNode1 = this.audioContext.createGain();
      const gainNode2 = this.audioContext.createGain();
      const mainGain = this.audioContext.createGain();
      
      // Configurar ganancia
      gainNode1.gain.value = 0.5;
      gainNode2.gain.value = 0.5;
      mainGain.gain.value = volume;
      
      // Conectar nodos
      osc1.connect(gainNode1);
      osc2.connect(gainNode2);
      gainNode1.connect(mainGain);
      gainNode2.connect(mainGain);
      mainGain.connect(this.audioContext.destination);
      
      // Iniciar osciladores
      osc1.start();
      osc2.start();
      
      // Guardar referencia a los osciladores y nodos activos
      this.activeOscillators.push(osc1, osc2);
      this.activeGainNodes.push(gainNode1, gainNode2, mainGain);
      
      // Si tiene duración, detener después de tiempo
      if (duration > 0) {
        setTimeout(() => {
          this.stopTone([osc1, osc2], [gainNode1, gainNode2, mainGain]);
        }, duration * 1000);
      }
      
      return {
        oscillators: [osc1, osc2],
        gainNodes: [gainNode1, gainNode2, mainGain]
      };
    } catch (error) {
      console.error('Error al generar tono:', error);
      return null;
    }
  }
  
  /**
   * Detiene osciladores y desconecta nodos de ganancia
   * @param {Array} oscillators - Array de osciladores a detener
   * @param {Array} gainNodes - Array de nodos de ganancia a desconectar
   */
  stopTone(oscillators, gainNodes) {
    try {
      // Detener osciladores
      if (oscillators && oscillators.length) {
        oscillators.forEach(osc => {
          if (osc && osc.stop) {
            osc.stop();
          }
        });
      }
      
      // Desconectar nodos de ganancia
      if (gainNodes && gainNodes.length) {
        gainNodes.forEach(gain => {
          if (gain && gain.disconnect) {
            gain.disconnect();
          }
        });
      }
      
      // Eliminar de los arrays activos
      this.activeOscillators = this.activeOscillators.filter(osc => 
        !oscillators.includes(osc)
      );
      this.activeGainNodes = this.activeGainNodes.filter(gain => 
        !gainNodes.includes(gain)
      );
    } catch (error) {
      console.error('Error al detener tono:', error);
    }
  }
  
  /**
   * Detiene todos los tonos activos
   */
  stopAllTones() {
    try {
      // Detener todos los osciladores
      if (this.activeOscillators.length) {
        this.activeOscillators.forEach(osc => {
          if (osc && osc.stop) {
            try {
              osc.stop();
            } catch (e) {
              // Ignorar error si ya está detenido
            }
          }
        });
      }
      
      // Desconectar todos los nodos de ganancia
      if (this.activeGainNodes.length) {
        this.activeGainNodes.forEach(gain => {
          if (gain && gain.disconnect) {
            try {
              gain.disconnect();
            } catch (e) {
              // Ignorar error si ya está desconectado
            }
          }
        });
      }
      
      // Limpiar arrays
      this.activeOscillators = [];
      this.activeGainNodes = [];
      
      // Detener tonos de sistema
      this.stopDialTone();
      this.stopRingTone();
      this.stopBusyTone();
      
      // Detener intervalos
      if (this.dialToneIntervalId) {
        clearInterval(this.dialToneIntervalId);
        this.dialToneIntervalId = null;
      }
      if (this.ringToneIntervalId) {
        clearInterval(this.ringToneIntervalId);
        this.ringToneIntervalId = null;
      }
      if (this.busyToneIntervalId) {
        clearInterval(this.busyToneIntervalId);
        this.busyToneIntervalId = null;
      }
    } catch (error) {
      console.error('Error al detener todos los tonos:', error);
    }
  }
  
  /**
   * Reproduce un tono DTMF (para el teclado numérico)
   * @param {String} digit - Dígito ('0'-'9', '*', '#')
   * @param {Number} duration - Duración en segundos
   */
  playDTMF(digit, duration = 0.15) {
    if (!this.isSupported || !this.dtmfFrequencies[digit]) return;
    
    const freqs = this.dtmfFrequencies[digit];
    this.generateTone(freqs[0], freqs[1], duration, 0.15);
  }
  
  /**
   * Inicia el tono de marcado (continuo)
   */
  startDialTone() {
    if (!this.isSupported || this.dialToneActive) return;
    
    const freqs = this.specialTones.dial;
    const tone = this.generateTone(freqs[0], freqs[1], 0, 0.1);
    this.dialToneActive = true;
    
    return tone;
  }
  
  /**
   * Detiene el tono de marcado
   */
  stopDialTone() {
    if (!this.isSupported || !this.dialToneActive) return;
    
    this.dialToneActive = false;
  }
  
  /**
   * Inicia el tono de timbre (patrón intermitente)
   */
  startRingTone() {
    if (!this.isSupported || this.ringToneActive) return;
    
    const ringPattern = () => {
      const freqs = this.specialTones.ring;
      const tone = this.generateTone(freqs[0], freqs[1], 1, 0.15);
    };
    
    // Patrón: 1 segundo sonando, 2 segundos en silencio
    ringPattern();
    this.ringToneIntervalId = setInterval(ringPattern, 3000);
    this.ringToneActive = true;
  }
  
  /**
   * Detiene el tono de timbre
   */
  stopRingTone() {
    if (!this.isSupported || !this.ringToneActive) return;
    
    if (this.ringToneIntervalId) {
      clearInterval(this.ringToneIntervalId);
      this.ringToneIntervalId = null;
    }
    this.ringToneActive = false;
  }
  
  /**
   * Reproduce el tono de fin de llamada
   */
  playEndTone() {
    if (!this.isSupported) return;
    
    const freqs = this.specialTones.end;
    this.generateTone(freqs[0], freqs[1], 0.5, 0.2);
  }
  
  /**
   * Inicia el tono de ocupado (patrón intermitente)
   */
  startBusyTone() {
    if (!this.isSupported || this.busyToneActive) return;
    
    const busyPattern = () => {
      const freqs = this.specialTones.busy;
      const tone = this.generateTone(freqs[0], freqs[1], 0.5, 0.15);
    };
    
    // Patrón: 0.5 segundos sonando, 0.5 segundos en silencio
    busyPattern();
    this.busyToneIntervalId = setInterval(busyPattern, 1000);
    this.busyToneActive = true;
  }
  
  /**
   * Detiene el tono de ocupado
   */
  stopBusyTone() {
    if (!this.isSupported || !this.busyToneActive) return;
    
    if (this.busyToneIntervalId) {
      clearInterval(this.busyToneIntervalId);
      this.busyToneIntervalId = null;
    }
    this.busyToneActive = false;
  }
  
  /**
   * Reanuda el contexto de audio (necesario en algunos navegadores)
   * Debe llamarse como respuesta a una interacción del usuario
   */
  resumeAudioContext() {
    if (this.isSupported && this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(err => {
        console.error('Error al reanudar contexto de audio:', err);
      });
    }
  }
}

export default new AudioService();